import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';

const styles = {
    container: { maxWidth: '700px', margin: '0 auto', padding: '32px 24px', color: '#e0e0e0' },
    title: { fontSize: '26px', fontWeight: 700, color: '#ffffff', marginBottom: '28px' },
    section: { marginBottom: '28px' },
    sectionTitle: { fontSize: '16px', fontWeight: 600, color: '#bb86fc', marginBottom: '14px', borderBottom: '1px solid #333', paddingBottom: '6px' },
    label: { display: 'block', marginBottom: '6px', fontSize: '14px', color: '#aaa' },
    input: { width: '100%', padding: '10px 12px', backgroundColor: '#2a2a2a', border: '1px solid #444', borderRadius: '6px', color: '#e0e0e0', fontSize: '15px', boxSizing: 'border-box' },
    textarea: { width: '100%', padding: '10px 12px', backgroundColor: '#2a2a2a', border: '1px solid #444', borderRadius: '6px', color: '#e0e0e0', fontSize: '15px', boxSizing: 'border-box', minHeight: '100px', resize: 'vertical' },
    row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
    row3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' },
    fieldGroup: { marginBottom: '16px' },
    addRowBtn: { background: 'none', border: '1px dashed #555', borderRadius: '6px', color: '#bb86fc', padding: '8px 16px', cursor: 'pointer', fontSize: '13px', marginTop: '8px' },
    removeBtn: { background: 'none', border: 'none', color: '#cf6679', cursor: 'pointer', fontSize: '18px', padding: '0 6px', lineHeight: 1 },
    linkRow: { display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' },
    linkInput: { flex: 1, padding: '10px 12px', backgroundColor: '#2a2a2a', border: '1px solid #444', borderRadius: '6px', color: '#e0e0e0', fontSize: '14px' },
    buttonRow: { display: 'flex', gap: '12px', marginTop: '32px' },
    saveBtn: { backgroundColor: '#bb86fc', color: '#000', border: 'none', borderRadius: '6px', padding: '12px 28px', fontWeight: 600, cursor: 'pointer', fontSize: '15px' },
    cancelBtn: { backgroundColor: 'transparent', color: '#aaa', border: '1px solid #444', borderRadius: '6px', padding: '12px 28px', cursor: 'pointer', fontSize: '15px' },
    errorBox: { backgroundColor: '#3a1a1a', border: '1px solid #cf6679', borderRadius: '6px', padding: '12px 16px', color: '#cf6679', marginBottom: '20px' }
};

export default function AddMember() {
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: '', bio: '', image_url: '', hometown_city: '', hometown_state: '', hometown_country: '', sharc_id: '' });
    const [links, setLinks] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const setField = (field, value) => setForm(f => ({ ...f, [field]: value }));

    const addLink = () => setLinks(l => [...l, { description: '', url: '' }]);
    const updateLink = (i, field, value) => setLinks(l => l.map((x, idx) => idx === i ? { ...x, [field]: value } : x));
    const removeLink = (i) => setLinks(l => l.filter((_, idx) => idx !== i));

    const handleSubmit = async () => {
        if (!form.name.trim()) { setError('Member name is required.'); return; }
        setSaving(true);
        setError(null);
        try {
            const res = await axios.post(`${config.API_URL}/members`, {
                ...form,
                links: links.filter(l => l.description.trim() && l.url.trim())
            });
            navigate(`/members/${res.data.member.slug}`);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to save member.');
            setSaving(false);
        }
    };

    return (
        <div style={styles.container}>
            <h1 style={styles.title}>Add Member</h1>

            {error && <div style={styles.errorBox}>{error}</div>}

            <div style={styles.section}>
                <div style={styles.sectionTitle}>Basic Info</div>
                <div style={styles.fieldGroup}>
                    <label style={styles.label}>Name *</label>
                    <input style={styles.input} value={form.name} onChange={e => setField('name', e.target.value)} placeholder="Full name or stage name" />
                </div>
                <div style={styles.fieldGroup}>
                    <label style={styles.label}>Bio</label>
                    <textarea style={styles.textarea} value={form.bio} onChange={e => setField('bio', e.target.value)} placeholder="Brief biography..." />
                </div>
                <div style={styles.fieldGroup}>
                    <label style={styles.label}>Image URL</label>
                    <input style={styles.input} value={form.image_url} onChange={e => setField('image_url', e.target.value)} placeholder="https://..." />
                </div>
            </div>

            <div style={styles.section}>
                <div style={styles.sectionTitle}>Hometown</div>
                <div style={styles.row3}>
                    <div style={styles.fieldGroup}>
                        <label style={styles.label}>City</label>
                        <input style={styles.input} value={form.hometown_city} onChange={e => setField('hometown_city', e.target.value)} placeholder="City" />
                    </div>
                    <div style={styles.fieldGroup}>
                        <label style={styles.label}>State / Region</label>
                        <input style={styles.input} value={form.hometown_state} onChange={e => setField('hometown_state', e.target.value)} placeholder="State" />
                    </div>
                    <div style={styles.fieldGroup}>
                        <label style={styles.label}>Country</label>
                        <input style={styles.input} value={form.hometown_country} onChange={e => setField('hometown_country', e.target.value)} placeholder="Country" />
                    </div>
                </div>
            </div>

            <div style={styles.section}>
                <div style={styles.sectionTitle}>SHARC ID</div>
                <div style={styles.fieldGroup}>
                    <label style={styles.label}>Vanity ID (optional)</label>
                    <input style={styles.input} value={form.sharc_id} onChange={e => setField('sharc_id', e.target.value)} placeholder="e.g. andyslania" />
                </div>
            </div>

            <div style={styles.section}>
                <div style={styles.sectionTitle}>Links</div>
                {links.map((link, i) => (
                    <div key={i} style={styles.linkRow}>
                        <input style={{ ...styles.linkInput, maxWidth: '160px' }} value={link.description} onChange={e => updateLink(i, 'description', e.target.value)} placeholder="e.g. Bandcamp" />
                        <input style={styles.linkInput} value={link.url} onChange={e => updateLink(i, 'url', e.target.value)} placeholder="https://..." />
                        <button style={styles.removeBtn} onClick={() => removeLink(i)}>×</button>
                    </div>
                ))}
                <button style={styles.addRowBtn} onClick={addLink}>+ Add Link</button>
            </div>

            <div style={styles.buttonRow}>
                <button style={styles.saveBtn} onClick={handleSubmit} disabled={saving}>{saving ? 'Saving...' : 'Save Member'}</button>
                <button style={styles.cancelBtn} onClick={() => navigate('/members')}>Cancel</button>
            </div>
        </div>
    );
}
